import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CalendarDots, ShoppingCart, NotePencil, Target, DotsThreeOutline, BookOpenText, Baby, ListChecks, ChatCircle, House, ClipboardText, TrendUp, Ruler, Drop, PersonSimpleRun, Footprints, Syringe, Heartbeat, Bell, Compass, Flower, BookOpen, ForkKnife, FolderOpen, Pill, Star, Question, Wallet, Receipt, Gear, SignOut, TestTube, Scroll } from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import FirstLoginOnboarding from '@/components/FirstLoginOnboarding';
import ConsultationFeedback from '@/components/ConsultationFeedback';
import { PublicFooter } from '@/components/PublicFooter';
import AccessGate from '@/components/AccessGate';
import { isBabyPatient, isGravidaPatient, isPosPartoPatient, isPreConcPatient } from '@/lib/utils';

const DEFAULT_NAV = [
  { path: '/dashboard', label: 'Início', icon: House },
  { path: '/ementa', label: 'Ementa', icon: CalendarDots },
  { path: '/programa', label: 'Plano', icon: ClipboardText },
  { path: '/compras', label: 'Compras', icon: ShoppingCart },
];

const IA_NAV = [
  { path: '/dashboard', label: 'Início', icon: House },
  { path: '/ementa-bebe', label: 'Ementa', icon: CalendarDots },
  { path: '/checklist-intro', label: 'Checklist', icon: ListChecks },
];

// Grupos 2 (Grávida/Pós-parto/Pré-conceção) e 3 (Emagrecimento/Geral/sem tipo).
// Glicemia e Tensão arterial só aparecem no grupo 2 (flag). Ciclo menstrual só em pós-parto/pré-conceção.
const BOTH_MORE = [
  { path: '/ementa-bebe', label: 'Ementa Bebé', group: 'acompanhamento' },
  { path: '/checklist-intro', label: 'Checklist introdução', group: 'acompanhamento' },
];

const FULL_MORE = [
  { path: '/diario', label: 'Diário alimentar', group: 'acompanhamento' },
  { path: '/checkin', label: 'Check-in semanal', group: 'acompanhamento' },
  { path: '/evolucao', label: 'Evolução', group: 'acompanhamento' },
  { path: '/perimetros', label: 'Os meus perímetros', group: 'acompanhamento' },
  { path: '/agua', label: 'Hidratação', group: 'acompanhamento' },
  { path: '/glicemia', label: 'Glicemia', group: 'acompanhamento', flag: 'gest_only' },
  { path: '/clinico', label: 'Tensão arterial', group: 'acompanhamento', flag: 'gest_only' },
  { path: '/analises', label: 'Análises', group: 'acompanhamento' },
  { path: '/atividade', label: 'Atividade física', group: 'acompanhamento' },
  { path: '/passos', label: 'Passos diários', group: 'acompanhamento' },
  { path: '/metas', label: 'Metas e Desafios', group: 'acompanhamento' },
  { path: '/ciclo', label: 'Ciclo menstrual', group: 'acompanhamento', conditional: 'ciclo' },
  { path: '/alertas', label: 'Lembretes', group: 'acompanhamento' },
  { path: '/onboarding', label: 'O meu programa', group: 'acompanhamento' },
  { path: '/suplementacao', label: 'Suplementação', group: 'conteudos' },
  { path: '/guia', label: 'Guia de orientações', group: 'conteudos', conditional: 'guia' },
  { path: '/minhas-receitas', label: 'Receitas sugeridas', group: 'conteudos' },
  { path: '/receitas', label: 'Receitas', group: 'conteudos' },
  { path: '/conteudos', label: 'Materiais', group: 'conteudos' },
  { path: '/recomendacoes', label: 'Recomendações', group: 'conteudos' },
  { path: '/como-usar', label: 'Como usar', group: 'conteudos' },
  { path: '/pagamentos', label: 'Pagamentos', group: 'conta' },
  { path: '/recibos', label: 'As minhas faturas', group: 'conta' },
  { path: '/definicoes', label: 'Definições', group: 'conta' },
  { path: '/informacao-legal', label: 'Informação legal', group: 'conta' },
  { path: '/logout', label: 'Sair', group: 'conta', action: 'logout' },
];

// Grupo 1 (Introdução alimentar): sem Tensão, Passos, Atividade, Perímetros, Metas e Desafios, Glicemia.
const IA_MORE = [
  { path: '/diario', label: 'Diário alimentar', group: 'acompanhamento' },
  { path: '/checklist-intro', label: 'Checklist introdução', group: 'acompanhamento' },
  { path: '/evolucao', label: 'Evolução', group: 'acompanhamento' },
  { path: '/agua', label: 'Hidratação', group: 'acompanhamento' },
  { path: '/checkin', label: 'Check-in semanal', group: 'acompanhamento' },
  { path: '/analises', label: 'Análises', group: 'acompanhamento' },
  { path: '/alertas', label: 'Lembretes', group: 'acompanhamento' },
  { path: '/onboarding', label: 'O meu programa', group: 'acompanhamento' },
  { path: '/suplementacao', label: 'Suplementação', group: 'conteudos' },
  { path: '/guia', label: 'Guia de orientações', group: 'conteudos', conditional: 'guia' },
  { path: '/minhas-receitas', label: 'Receitas sugeridas', group: 'conteudos' },
  { path: '/receitas', label: 'Receitas', group: 'conteudos' },
  { path: '/conteudos', label: 'Materiais', group: 'conteudos' },
  { path: '/recomendacoes', label: 'Recomendações', group: 'conteudos' },
  { path: '/como-usar', label: 'Como usar', group: 'conteudos' },
  { path: '/pagamentos', label: 'Pagamentos', group: 'conta' },
  { path: '/recibos', label: 'As minhas faturas', group: 'conta' },
  { path: '/definicoes', label: 'Definições', group: 'conta' },
  { path: '/informacao-legal', label: 'Informação legal', group: 'conta' },
  { path: '/logout', label: 'Sair', group: 'conta', action: 'logout' },
];

const GROUP_LABELS = { acompanhamento: 'Acompanhamento', conteudos: 'Conteúdos', conta: 'Conta' };

const MORE_ICONS = {
  '/diario': NotePencil, '/checkin': ClipboardText, '/checklist-intro': ListChecks, '/ementa-bebe': CalendarDots,
  '/evolucao': TrendUp, '/perimetros': Ruler, '/agua': Drop, '/atividade': PersonSimpleRun,
  '/passos': Footprints, '/glicemia': Syringe, '/clinico': Heartbeat, '/analises': TestTube, '/alertas': Bell,
  '/onboarding': Compass, '/ciclo': Flower, '/metas': Target, '/minhas-receitas': BookOpen,
  '/receitas': ForkKnife, '/conteudos': FolderOpen, '/suplementacao': Pill, '/guia': BookOpenText, '/recomendacoes': Star,
  '/como-usar': Question, '/pagamentos': Wallet, '/recibos': Receipt, '/definicoes': Gear, '/informacao-legal': Scroll, '/logout': SignOut,
};

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showMore, setShowMore] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setShowMore(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const babyPatient = isBabyPatient(user);
  const hasBoth = babyPatient && user?.has_adult_plan === true;
  const isIA = babyPatient && !hasBoth;
  const isGest = !isIA && (isGravidaPatient(user) || isPosPartoPatient(user) || isPreConcPatient(user));
  const navItems = isIA ? IA_NAV : DEFAULT_NAV;
  const moreItems = isIA ? IA_MORE : (hasBoth ? [...FULL_MORE, ...BOTH_MORE] : FULL_MORE);
  // Grupo 2 (grávida/pós-parto/pré-conceção): mostra Glicemia e Tensão. Grupo 3: esconde-as.
  // Ciclo menstrual: só pós-parto e pré-conceção.
  const inactiveFollowup = ['alta', 'pausa', 'terminou_sem_alta'].includes(user?.followup_state) || user?.active_followup === false;
  const filteredMore = moreItems.filter(m => {
    if (m.path === '/checkin' && inactiveFollowup) return false;
    if (m.flag === 'gest_only') return isGest;
    if (m.conditional === 'guia') return !!user?.has_guide;
    if (m.conditional === 'ciclo') return isPosPartoPatient(user) || isPreConcPatient(user);
    return true;
  });

  const isActive = (path) => location.pathname === path;
  const isMoreActive = filteredMore.some(item => location.pathname === item.path);
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => { if (user?.first_login === true) setShowOnboarding(true); }, [user]);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="app-layout">
      {showOnboarding && <FirstLoginOnboarding onComplete={() => setShowOnboarding(false)} />}
      {!showOnboarding && <ConsultationFeedback />}
      {/* Top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-brand-bg/80 border-b border-brand-border/60">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="font-heading font-semibold text-lg text-brand-green tracking-tight" data-testid="app-logo">
            nutrianapereira
          </h1>
          <div className="flex items-center gap-3">
            {user && (
              <>
                <button onClick={() => window.location.href = '/mensagens'} className="relative p-1.5 text-brand-muted hover:text-brand-green" data-testid="header-messages-btn">
                  <ChatCircle size={20} />
                </button>
                <span className="text-sm text-brand-muted hidden sm:block">{user.preferred_name || user.name || user.email}</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 py-6 pb-28">
        <AccessGate>{children}</AccessGate>
        <PublicFooter compact />
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-brand-border flex justify-around py-3 pb-safe z-50 rounded-t-3xl shadow-[0_-8px_32px_rgba(0,0,0,0.03)]" data-testid="bottom-nav">
        {navItems.map(({ path, label, icon: Icon }) => (
          <Link
            key={path}
            to={path}
            className={`flex flex-col items-center gap-1 transition-colors no-underline ${isActive(path) ? 'text-brand-green' : 'text-brand-placeholder hover:text-brand-muted'}`}
            data-testid={`nav-${label.toLowerCase()}`}
          >
            <Icon size={24} weight={isActive(path) ? 'duotone' : 'regular'} />
            <span className="text-xs font-medium">{label}</span>
          </Link>
        ))}
        {/* More button */}
        <div className="relative" ref={moreRef}>
          <button
            onClick={() => setShowMore(!showMore)}
            className={`flex flex-col items-center gap-1 transition-colors ${isMoreActive || showMore ? 'text-brand-green' : 'text-brand-placeholder hover:text-brand-muted'}`}
            data-testid="nav-mais"
          >
            <DotsThreeOutline size={24} weight={isMoreActive || showMore ? 'duotone' : 'regular'} />
            <span className="text-xs font-medium">Mais</span>
          </button>
          {showMore && (
            <div className="absolute bottom-full right-0 mb-2 w-[19rem] max-w-[92vw] bg-white border border-brand-border rounded-2xl shadow-lg overflow-hidden max-h-[72vh] overflow-y-auto p-2" data-testid="more-menu">
              {['acompanhamento', 'conteudos', 'conta'].map(group => {
                const items = filteredMore.filter(m => m.group === group);
                if (items.length === 0) return null;
                return (
                  <div key={group} className="mb-1">
                    <p className="px-2 pt-2 pb-1.5 text-[9px] text-brand-muted uppercase tracking-wider font-medium">{GROUP_LABELS[group]}</p>
                    <div className="grid grid-cols-3 gap-1">
                      {items.map(({ path, label, action }) => {
                        const Icon = MORE_ICONS[path] || DotsThreeOutline;
                        const active = isActive(path);
                        const tileCls = `flex flex-col items-center justify-center gap-1.5 rounded-xl px-1 py-3 text-center transition-colors no-underline ${active ? 'bg-brand-mint/20 text-brand-green' : (action === 'logout' ? 'text-red-500 hover:bg-red-50' : 'text-brand-text hover:bg-brand-bg')}`;
                        const inner = (<><Icon size={22} weight={active ? 'duotone' : 'regular'} /><span className="text-[10px] leading-tight font-medium">{label}</span></>);
                        return action === 'logout'
                          ? <button key={path} onClick={() => { logout(); setShowMore(false); }} className={tileCls} data-testid={`more-${label.toLowerCase()}`}>{inner}</button>
                          : <Link key={path} to={path} onClick={() => setShowMore(false)} className={tileCls} data-testid={`more-${label.toLowerCase()}`}>{inner}</Link>;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </nav>
    </div>
  );
}
